import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { BookOpen, CakeSlice, ChevronLeft, Clapperboard, FolderOpen, PenLine, Pencil, Plus, Search, Settings2, Trash2, ArrowRight } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import EditorialVisualCard from "@/pages/admin/EditorialVisualCard";
import { ALMOND_PUBLICATION_SPACES } from "@/lib/almondPublicationCatalogV1.mjs";
import { isEditorialTechnicalSample, bookPlacementByMaterial } from "@/lib/editorialVisualCatalogV2.mjs";
import { availableShowcaseMaterial, editorialShowcaseEntry } from "@/lib/editorialShowcaseEntryV1.mjs";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  EDITORIAL_WORKSPACES, OPEN_LIBRARY_EDITORIAL_SECTIONS,
  productForEditorialWorkspace, editorialVolumesForWorkspace,
  isTechnicalEditorialEdition, isSafeEditorialCreationTarget,
  canOfferEditorialDraftDelete,
} from "@/lib/editorialWorkspaceNavigationV1.mjs";

// UI-only entry point. Nothing is automatically linked, migrated or published.
const API = "/admin/editorial";
const ICONS = {
  "open-library": BookOpen, almond: CakeSlice, medovik: BookOpen,
  bento: CakeSlice, lineart: Pencil, gingerbread: FolderOpen, "media-studio": Clapperboard,
};
const GROUPS = [
  ["library", "Открытая библиотека"],
  ["programs", "Авторские программы"],
  ["workshops", "Другие мастерские"],
];

export default function EditorialWorkspaceHub() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const workspace = EDITORIAL_WORKSPACES.find((x) => x.key === params.get("space")) || null;
  const [data, setData] = useState({ editions: [], volumes: [], materials: [], products: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [section, setSection] = useState("all");
  const [almondSpace, setAlmondSpace] = useState("");
  const [almondCategory, setAlmondCategory] = useState("");
  const [bookPlacements, setBookPlacements] = useState(new Map());
  const [blocksByMaterial, setBlocksByMaterial] = useState({});
  const [pageLimit, setPageLimit] = useState(18);
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newVolumeId, setNewVolumeId] = useState("");
  const [newSection, setNewSection] = useState("RECIPES");
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const isOwner = user?.role === "owner";
  const isAdmin = isOwner || user?.role === "administrator";
  const scopes = user?.product_scope_ids || [];

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [editions, volumes, materials, products] = await Promise.all([
        api.get(API + "/editions"), api.get(API + "/volumes"),
        api.get(API + "/materials"),
        isAdmin ? api.get("/admin/products").catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      ]);
      setData({
        editions: Array.isArray(editions.data) ? editions.data : [],
        volumes: Array.isArray(volumes.data) ? volumes.data : [],
        materials: Array.isArray(materials.data) ? materials.data : [],
        products: Array.isArray(products.data) ? products.data : [],
      });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);
  useEffect(() => { load(); }, [load]);

  const choose = (key) => {
    setParams(key ? { space: key } : {});
    setSearch("");
    setStatus("all");
    setSection("all");
    setAlmondSpace("");
    setAlmondCategory("");
    setPageLimit(18);
    setCreating(false);
    setToDelete(null);
  };
  const product = workspace ? productForEditorialWorkspace(workspace.key, data.products) : null;
  const authorizedWorkspace = !workspace || isOwner || !scopes.length || workspace.key === "open-library" || (!!product && scopes.includes(product.id));
  const volumes = workspace ? editorialVolumesForWorkspace(workspace.key, data.volumes, data.products) : [];
  const editionById = new Map(data.editions.map((e) => [e.id, e]));
  const workingVolumes = volumes.filter((v) => {
    const edition = editionById.get(v.edition_id);
    return edition && !isTechnicalEditorialEdition(edition);
  });
  const createVolumes = workingVolumes.filter((v) =>
    isSafeEditorialCreationTarget(workspace?.key, v, data.editions, data.products));
  const knownIds = new Set(volumes.map((v) => v.id));
  const allMaterials = data.materials.filter((m) => knownIds.has(m.volume_id) && m.status !== "archived");
  const filteredMaterials = allMaterials.filter((m) =>
    (!search || String(m.title || "").toLocaleLowerCase("ru-RU").includes(search.toLocaleLowerCase("ru-RU")))
    && (status === "all" || m.status === status)
    && (workspace?.key !== "open-library" || section === "all" || m.open_library_section === section));
  const technicalMaterials = filteredMaterials.filter((m) => isEditorialTechnicalSample(m));
  const realMaterials = filteredMaterials.filter((m) => !technicalMaterials.some((t) => t.id === m.id));
  const catalogMaterials = realMaterials.filter((m) => workspace?.key !== "almond" || !almondSpace || (bookPlacements.get(m.id)?.spaceKey === almondSpace && (!almondCategory || bookPlacements.get(m.id)?.categoryId === almondCategory)));
  const shownMaterials = catalogMaterials.slice(0, pageLimit);
  const imageMaterialIds = shownMaterials.map((m) => m.id).join("|");
  const selectedCreateId = createVolumes.length === 1 ? createVolumes[0].id : newVolumeId;

  useEffect(() => {
    if (workspace?.key !== "almond" || !product?.id) { setBookPlacements(new Map()); return; }
    let active = true;
    (async () => {
      try {
        const [volumesRes, sectionsRes, subsectionsRes, pagesRes] = await Promise.all([
          api.get("/admin/book/volumes"), api.get("/admin/book/sections"),
          api.get("/admin/book/subsections"), api.get("/admin/book/pages"),
        ]);
        const vol = (volumesRes.data || []).find((v) => v.product_id === product.id);
        if (!vol) { if (active) setBookPlacements(new Map()); return; }
        const sections = (sectionsRes.data || []).filter((v) => v.volume_id === vol.id);
        const subsections = (subsectionsRes.data || []).filter((v) => v.volume_id === vol.id);
        const pages = (pagesRes.data || []).filter((v) => v.volume_id === vol.id);
        if (active) setBookPlacements(bookPlacementByMaterial(pages, sections, subsections));
      } catch { if (active) setBookPlacements(new Map()); }
    })();
    return () => { active = false; };
  }, [workspace?.key, product?.id]);

  useEffect(() => {
    if (!workspace || !imageMaterialIds) { setBlocksByMaterial({}); return; }
    let active = true;
    const ids = imageMaterialIds.split("|").filter(Boolean);
    Promise.all(ids.map(async (id) => {
      try { const { data: blocks } = await api.get(API + "/materials/" + encodeURIComponent(id) + "/blocks");
        return [id, Array.isArray(blocks) ? blocks : []]; }
      catch { return [id, []]; }
    })).then((pairs) => { if (active) setBlocksByMaterial(Object.fromEntries(pairs)); });
    return () => { active = false; };
  }, [workspace?.key, imageMaterialIds]);

  const openMaterial = (id) => {
    const suffix = workspace ? "?from_workspace=" + encodeURIComponent(workspace.key) : "";
    nav("/admin/editorial-v2/materials/" + encodeURIComponent(id) + suffix, { state: { returnSpace: workspace?.key } });
  };
  const createDraft = async (event) => {
    event.preventDefault();
    const volume = createVolumes.find((v) => v.id === selectedCreateId);
    if (!newTitle.trim() || !volume || saving) return;
    setSaving(true);
    try {
      const payload = { volume_id: volume.id, title: newTitle.trim(), status: "draft" };
      if (workspace.key === "open-library") payload.open_library_section = newSection;
      const { data: created } = await api.post(API + "/materials", payload);
      setCreating(false);
      openMaterial(created.id);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };
  const deleteDraft = async () => {
    if (!toDelete || deleting) return;
    setDeleting(true);
    try {
      if (toDelete.archiveMode) {
        const { data: response } = await api.post(API + "/materials/" + encodeURIComponent(toDelete.id) + "/archive-internal-draft");
        if (response?.action !== "archived") throw new Error("Архивация не подтверждена");
        toast.success("Черновик перемещён в архив");
      } else {
        await api.delete(API + "/materials/" + encodeURIComponent(toDelete.id));
        toast.success("Черновик удалён");
      }
      setToDelete(null);
      await load();
    } catch (e) {
      const code = e?.response?.data?.detail;
      if (code === "editorial_draft_has_publication") {
        try {
          const { data: eligibility } = await api.get(API + "/materials/" + encodeURIComponent(toDelete.id) + "/archive-internal-draft/eligibility");
          if (eligibility?.eligible === true) {
            setToDelete((previous) => previous ? { ...previous, archiveMode: true } : null);
            return;
          }
        } catch { /* no archive without confirmed eligibility */ }
      }
      toast.error(code === "editorial_draft_has_publication" ? "Материал связан с публикацией и защищён от удаления."
        : code === "editorial_draft_protected_fixture" ? "Служебный материал нельзя удалить."
        : formatApiError(e));
    } finally {
      setDeleting(false);
    }
  };

  const showSharedEditor = isOwner && !!workspace;
  const showcaseAvailable = availableShowcaseMaterial(data.materials);
  const openSharedEditor = () => {
    if (!showSharedEditor || !showcaseAvailable) return;
    const target = editorialShowcaseEntry(workspace.key);
    if (target) nav(target.path, { state: target.state });
  };

  const availableSpaces = EDITORIAL_WORKSPACES.filter((s) => {
    if (isOwner || !scopes.length || s.key === "open-library") return true;
    const p = productForEditorialWorkspace(s.key, data.products);
    return !!p && scopes.includes(p.id);
  });

  return <div className="min-h-screen bg-cream" data-testid="editorial-workspace-hub">
    <header className="bg-white/40">
      <div className="max-w-6xl mx-auto px-5 py-4 flex items-center gap-3">
        <button type="button" onClick={() => workspace ? choose("") : nav("/admin")}
          aria-label={workspace ? "Все пространства Редакции" : "Панель управления"}
          className="p-2 -ml-2 rounded-full hover:bg-sand/50"><ChevronLeft className="h-5 w-5 text-espresso" /></button>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Anokina Studio · Редакция</div>
          <h1 className="font-serif text-2xl text-espresso truncate">{workspace ? workspace.title : "Мои публикации"}</h1>
        </div>
        {workspace && <button type="button" onClick={() => choose("")}
          className="rounded-xl bg-white/70 border hairline px-3 py-2 text-xs text-espresso">Все пространства</button>}
      </div>
    </header>
    <main className="max-w-6xl mx-auto px-5 py-7 space-y-6">
      {error && <div role="alert" className="rounded-xl bg-red-50 border border-red-200 p-4 text-red-800 text-sm">
        {error} <button type="button" onClick={load} className="underline">Повторить</button></div>}
      {loading && <p className="text-sm text-muted-foreground">Загрузка материалов…</p>}
      {!workspace && <>
        <div>
          <h2 className="font-serif text-2xl text-espresso">Выберите пространство</h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3" data-testid="editorial-compact-workspaces">
          {availableSpaces.map((s) => {
            const Icon = ICONS[s.key] || BookOpen;
            return <button type="button" key={s.key} onClick={() => choose(s.key)}
              data-testid={"editorial-workspace-" + s.key}
              className="min-h-[112px] sm:min-h-[128px] rounded-2xl border hairline bg-white/85 p-4 text-left flex flex-col items-start hover:bg-sand/25 focus-visible:outline focus-visible:outline-2 focus-visible:outline-espresso">
              <span className="rounded-full bg-sand/60 p-2"><Icon className="h-4 w-4 text-espresso" /></span>
              <span className="font-serif text-lg sm:text-xl text-espresso mt-2 leading-tight">{s.title}</span>
              <span className="text-[11px] text-espresso/60 mt-auto pt-1">{s.group === "library" ? "Библиотека" : s.group === "programs" ? "Авторская программа" : "Мастерская"}</span>
            </button>;
          })}
        </div>
        {isOwner && <button type="button" onClick={() => setParams({ technical: "1" })}
          data-testid="editorial-technical-mode"
          className="text-xs text-muted-foreground underline underline-offset-4 hover:text-espresso">
          <Settings2 className="inline h-3.5 w-3.5 mr-1" /> Служебные материалы и диагностика
        </button>}
      </>}
      {workspace && !authorizedWorkspace && <div role="alert" className="rounded-xl border hairline bg-white p-5 text-sm text-espresso">Нет доступа к редакционному пространству.</div>}
      {workspace && authorizedWorkspace && <section className="space-y-5" data-testid={"editorial-selected-" + workspace.key}>
        <p className="text-sm text-muted-foreground">{workspace.description}</p>
        {showSharedEditor &&
          <div className="rounded-xl border hairline bg-[#F4EEE4] px-4 py-3 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between"
            data-testid={"editorial-sample-editor-" + workspace.key}>
            <div>
              <div className="font-serif text-lg text-espresso">Редактор — образцы оформления</div>
              <p className="text-xs text-espresso/65 mt-1">
                Общая библиотека образцов. Открытие не создаёт публикацию в «{workspace.title}».
              </p>
            </div>
            <button type="button" onClick={openSharedEditor} disabled={!showcaseAvailable}
              title={!showcaseAvailable ? "Служебные образцы сейчас недоступны" : "Открыть общий редактор образцов"}
              className="rounded-lg border hairline bg-white px-4 py-2 text-sm shrink-0 hover:bg-sand/30 disabled:opacity-50"
              data-testid="editorial-open-sample-editor">
              Открыть редактор
            </button>
          </div>}
        {workspace.key === "almond" && <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3" data-testid="editorial-almond-four-spaces">
            {ALMOND_PUBLICATION_SPACES.map((s) => <button type="button" key={s.key}
              onClick={() => { setAlmondSpace(s.key); setAlmondCategory(""); setPageLimit(18); }}
              aria-pressed={almondSpace === s.key} data-testid={"editorial-almond-space-" + s.key}
              className={"min-h-[112px] rounded-2xl border p-4 text-left " + (almondSpace === s.key ? "border-[#AE906D] bg-[#EADCC8]" : "hairline bg-white/80 hover:bg-sand/25")}>
              <span className="text-[11px] text-espresso/55">{s.number}</span>
              <div className="mt-2 font-serif text-xl leading-tight text-espresso">{s.title}</div>
              <div className="mt-1 text-xs text-espresso/65">{s.subtitle}</div>
            </button>)}
          </div>
          {almondSpace && <div className="space-y-3 rounded-xl border hairline bg-white/60 p-4">
            <div className="flex justify-between items-center">
              <h3 className="font-serif text-xl text-espresso">{ALMOND_PUBLICATION_SPACES.find((s) => s.key === almondSpace)?.title}</h3>
              <button className="text-xs underline text-espresso" onClick={() => { setAlmondSpace(""); setAlmondCategory(""); }}>Все материалы</button>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="editorial-almond-categories">
              {ALMOND_PUBLICATION_SPACES.find((s) => s.key === almondSpace)?.categories.map((category) =>
                <button type="button" key={category.id} onClick={() => { setAlmondCategory(category.id === almondCategory ? "" : category.id); setPageLimit(18); }}
                  aria-pressed={almondCategory === category.id} className={"rounded-xl border hairline px-3 py-2 text-xs " + (almondCategory === category.id ? "bg-[#EADCC8]" : "bg-white")}>{category.title}</button>)}
              {almondSpace === "transformations" && <span className="text-xs text-espresso/70">Категории будут появляться по мере наполнения.</span>}
            </div>
          </div>}
          {product && <button type="button" onClick={() => nav("/admin/products/" + encodeURIComponent(product.id) + "?tab=content")}
            className="text-xs text-espresso underline underline-offset-4" data-testid="editorial-almond-contents">
            Открыть утверждённое содержание Тома I <ArrowRight className="inline h-3 w-3" />
          </button>}
        </div>}
        {workspace.key === "open-library" && <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="editorial-library-sections">
          {OPEN_LIBRARY_EDITORIAL_SECTIONS.map((s) => <button type="button" key={s.id}
            onClick={() => { setSection(section === s.id ? "all" : s.id); setPageLimit(18); }}
            aria-pressed={section === s.id}
            className={"rounded-xl border hairline text-left min-h-[54px] px-3 py-2 font-serif text-base " + (section === s.id ? "bg-[#EADCC8]" : "bg-white/75")}>{s.title}</button>)}
        </div>}
        <div className="flex flex-wrap gap-3 items-center justify-between">
          <h2 className="font-serif text-2xl text-espresso">Материалы</h2>
          {createVolumes.length > 0 && <Button type="button" onClick={() => { setNewTitle(""); setNewVolumeId(""); setCreating(true); }}
            data-testid="editorial-space-new-material"><Plus className="h-4 w-4 mr-2" /> Создать материал</Button>}
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <label className="flex flex-1 items-center gap-2 rounded-xl bg-white border hairline px-3 h-11">
            <Search className="h-4 w-4 text-muted-foreground" />
            <input type="search" value={search} onChange={(event) => setSearch(event.target.value)}
              placeholder="Поиск по названию" aria-label="Поиск материалов"
              className="w-full bg-transparent outline-none text-sm" data-testid="editorial-space-search" />
          </label>
          <div className="flex gap-2 flex-wrap">
            {[["all","Все"],["published","Опубликованные"],["draft","Черновики"]].map(([value,label]) =>
              <button type="button" key={value} onClick={() => setStatus(value)}
                aria-pressed={status === value} className={"rounded-full border hairline px-3 h-10 text-xs " + (status === value ? "bg-sand/70" : "bg-white")}>{label}</button>)}
          </div>
        </div>

        {!loading && catalogMaterials.length === 0 &&
          <div className="rounded-2xl border hairline bg-white/70 p-8 text-center">
            <h3 className="font-serif text-xl text-espresso">В этом разделе пока нет материалов</h3>
            <p className="text-sm text-muted-foreground mt-2">Публикации появятся здесь после назначения их в этот раздел.</p>
          </div>}
        {shownMaterials.length > 0 && <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="editorial-visual-card-grid">
          {shownMaterials.map((m) => {
            const canDelete = canOfferEditorialDraftDelete(m, user?.role);
            const deleteHelp = m.published_snapshot_id || m.status === "published" ? "Опубликованная версия защищена. Управляйте публикацией в редакторе." : "Черновик имеет историю или связи; безопасное удаление недоступно.";
            return <EditorialVisualCard key={m.id} material={m} blocks={blocksByMaterial[m.id] || []}
              workspaceKey={workspace.key} workspaceTitle={workspace.title} placement={bookPlacements.get(m.id)}
              onEdit={() => openMaterial(m.id)}
              canDelete={canDelete} deleteHelp={deleteHelp}
              onDelete={() => setToDelete({ id: m.id, title: m.title })} />;
          })}
        </div>}
        {catalogMaterials.length > shownMaterials.length && <button type="button"
          onClick={() => setPageLimit((n) => n + 18)} className="rounded-xl border hairline bg-white px-5 py-3 text-sm text-espresso">
          Показать ещё ({catalogMaterials.length - shownMaterials.length})
        </button>}
        {technicalMaterials.length > 0 && isOwner && <details className="rounded-xl border hairline bg-white/40 p-3" data-testid="editorial-technical-materials">
          <summary className="cursor-pointer text-xs text-espresso/60">Служебные и ранее созданные материалы ({technicalMaterials.length})</summary>
          <div className="mt-3 space-y-2">
            {technicalMaterials.map((m) => <div key={m.id} className="rounded-lg border hairline bg-white px-3 py-2 flex gap-3 items-center justify-between">
              <div className="min-w-0">
                <p className="font-serif text-sm text-espresso truncate">{isEditorialTechnicalSample(m) ? "Служебный материал редактора" : m.title}</p>
                <p className="text-[11px] text-espresso/50">Не переносится и не удаляется автоматически</p>
              </div>
              <button type="button" onClick={() => openMaterial(m.id)} className="rounded-lg border hairline px-3 py-2 text-xs shrink-0">Открыть</button>
            </div>)}
          </div>
        </details>}
      </section>}
    </main>
    <Dialog open={creating} onOpenChange={(open) => !open && !saving && setCreating(false)}>
      <DialogContent className="rounded-2xl" data-testid="editorial-space-create-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Новый материал</DialogTitle>
          <DialogDescription>Материал создаётся черновиком внутри выбранного пространства.</DialogDescription>
        </DialogHeader>
        <form onSubmit={createDraft} className="space-y-4">
          <label className="block text-sm text-espresso">Название
            <Input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} required
              maxLength={300} className="mt-1" data-testid="editorial-space-new-title" />
          </label>
          {createVolumes.length > 1 && <label className="block text-sm text-espresso">Редакционный раздел
            <select className="w-full h-11 mt-1 rounded-xl border hairline bg-white px-3"
              required value={newVolumeId} onChange={(e) => setNewVolumeId(e.target.value)}>
              <option value="">Выберите раздел</option>
              {createVolumes.map((v) => <option key={v.id} value={v.id}>{v.title}</option>)}
            </select>
          </label>}
          {workspace?.key === "open-library" && <label className="block text-sm text-espresso">Направление библиотеки
            <select className="w-full h-11 mt-1 rounded-xl border hairline bg-white px-3"
              value={newSection} onChange={(e) => setNewSection(e.target.value)}>
              {OPEN_LIBRARY_EDITORIAL_SECTIONS.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
            </select>
          </label>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setCreating(false)}>Отмена</Button>
            <Button type="submit" disabled={saving || !newTitle.trim() || !selectedCreateId}
              data-testid="editorial-space-create-confirm">{saving ? "Создание…" : "Создать черновик"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
    <Dialog open={!!toDelete} onOpenChange={(open) => !open && !deleting && setToDelete(null)}>
      <DialogContent data-testid="editorial-space-delete-dialog">
        <DialogHeader>
          <DialogTitle>{toDelete?.archiveMode ? "Переместить черновик в архив?" : "Удалить черновик?"}</DialogTitle>
          <DialogDescription>{toDelete?.archiveMode
            ? "Материал имеет историю. Архивация сохранит его данные и связи."
            : "Черновик «" + (toDelete?.title || "") + "» будет удалён без восстановления. Опубликованная история защищена сервером."}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="outline" disabled={deleting} onClick={() => setToDelete(null)}>Отмена</Button>
          <Button type="button" disabled={deleting} onClick={deleteDraft} className="bg-red-700 hover:bg-red-800"
            data-testid="editorial-space-delete-confirm">{deleting ? "Сохранение…" : toDelete?.archiveMode ? "Архивировать" : "Удалить черновик"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>;
}

function MaterialRow({ material, role, onEdit, onDelete }) {
  const mayEdit = material.status !== "archived" && !material.owner_lifecycle_operation;
  const mayDelete = !!onDelete && canOfferEditorialDraftDelete(material, role);
  const deleteHelp = material.published_snapshot_id || material.status === "published"
    ? "Материал имеет опубликованную версию. Сначала управляйте её публикацией в редакторе."
    : material.qa_fixture || material.e2e_fixture
      ? "Служебный материал защищён от удаления."
      : "Удаление недоступно: проверьте историю и связи материала.";
  return <article data-testid={"editorial-space-material-" + material.id}
    className="rounded-xl border hairline bg-white/80 px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
    <div className="flex-1 min-w-0">
      <h3 className="font-serif text-lg text-espresso">{material.title}</h3>
      <p className="text-xs text-muted-foreground mt-1">{material.published_snapshot_id || material.status === "published"
        ? (material.status === "draft" ? "Опубликованная версия сохранена · изменения в черновике" : "Опубликовано") : material.status === "archived" ? "Архив" : "Черновик"}</p>
    </div>
    <div className="flex gap-2 items-center shrink-0">
      <button type="button" onClick={onEdit} disabled={!mayEdit}
        data-testid={"editorial-space-edit-" + material.id}
        className="rounded-xl border hairline px-3 py-2 text-sm text-espresso hover:bg-sand/30 disabled:opacity-40">
        <PenLine className="inline w-3.5 h-3.5 mr-1" /> Редактировать
      </button>
      <button type="button" onClick={mayDelete ? onDelete : undefined} disabled={!mayDelete}
        title={mayDelete ? "Удалить черновик после подтверждения" : deleteHelp}
        aria-label={"Удалить черновик " + material.title}
        data-testid={"editorial-space-delete-" + material.id}
        className="rounded-xl border border-red-200 px-3 py-2 text-sm text-red-800 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed">
        <Trash2 className="inline w-3.5 h-3.5 mr-1" /> Удалить
      </button>
    </div>
  </article>;
}
